import cloneDeep from 'lodash/cloneDeep';
import isNil from 'lodash/isNil';
import toString from 'lodash/toString';
import parse from 'date-fns/parse';
import isValid from 'date-fns/isValid';
import isDate from 'date-fns/isDate';
import utcToZonedTime from 'date-fns-tz/utcToZonedTime';

import IProduct, { BatteryTypeEnum, IFoodlaCategory } from 'models/product';
import { IFoodlaCategoryOption } from 'models/category';
import { isNewFood, isSnusOrTobacco } from 'components/product/ProductUpdateForm/fields/category';
import {
  PRODUCT_BOOLEAN_FIELD,
  PRODUCT_BOOLEAN_FIELD_VALUES,
  PRODUCT_DATE_FIELD,
  PRODUCT_ENUM_FIELD,
  PRODUCT_ENUM_FIELD_VALUES,
  PRODUCT_NUMBER_FIELD,
  PRODUCT_EAN_FIELDS,
  animalCategoriesForFeed,
  securityNotices,
  hazardStatements,
  sunProtectionFactor,
  categoryWeightloss,
  preparedForm,
  signalWords,
  recyclingType,
  eggWeightClass,
  catchArea,
  fishCatchMethod,
  fishProductionMethod,
  DESCRIPTIVE_SIZE_UNITS_MAP,
  NUTRITIONAL_UNITS_MAP,
  additionalCharacteristics,
  approvalDietFeed,
  complianceMetalsSkinContact,
  confirmGuidanceSwedishElectronics,
  qrNutritionClaimsCode,
  qrHealthClaimsCode,
  qrElectronicCode,
  complianceBiocid,
  complianceSunscreenRegulation,
  complianceBiocidalTreated,
  complianceToyDirective,
  PRODUCT_CLASSIFICATIONS,
  animalFeedTypes,
  ALL_LABELS,
  ironings,
  containsBatteries,
  batteryTypes,
  batteryTechnologies,
  batteryHazardousMetals,
  supplementSubstanceUnits,
  dagsdosUnit,
  flammableSubtype,
} from 'components/constants-ts';
import { getCountryCode, hasValue, isBlank } from 'utils/helpers-ts';
import { DataType, IImportColumn, IProductExcel } from '../../types';
import { NULLABLE_FIELDS, VALID_DATE_FORMATS } from '../../constants';
import { hazardSymbols } from 'components/product/ProductUpdateForm/fields/hazard-fields';
import { checkDecimal, checkRequireDimenstions } from 'components/product/ProductUpdateForm/fields/gross-dimensions';
import { SHORT_SALES_DESCRIPTION_MAX_LENGTH } from 'components/product/ProductUpdateForm/fields/short-sales-description';
import { capitalizeTitle } from 'components/product/ProductUpdateForm/utils';

import { checkGrossWeightError } from 'components/product/ProductUpdateForm/fields/gross-weight';
import { checkDescriptiveError } from 'components/product/ProductUpdateForm/fields/descriptive';
import { checkEANValue } from 'components/product/ProductUpdateForm';
import { energyClassOptions } from 'components/product/ProductUpdateForm/fields/electric-lamp';
import { getNormalizedDocumentUrl } from 'components/product/ProductUpdateForm/fields/product-documents';
import { RECYCLING_AMOUNT, getPercentageValues } from 'components/constants';
import { FLOAT_FIELDS, INTEGER_FIELDS, NUMBER_RULE_FIELDS } from 'constants/bulk-import';
import { data as countryOfManufacturing } from 'components/product/ProductUpdateForm/fields/country-of-manufacturing';

export const parseDateString = (value: string) => {
  let validDate: Date | undefined;

  VALID_DATE_FORMATS.forEach(format => {
    if (validDate) return;

    const parsedDate = parse(value, format, new Date());
    if (!isValid(parsedDate)) return;

    validDate = parsedDate;
  });

  return validDate;
};

export const computeBaseItems = (dataList: IProductExcel[]) => {
  const computedDataList = cloneDeep(dataList);

  // base items
  for (let index = 0; index < computedDataList.length; index++) {
    const mappedData = computedDataList[index];
    const {
      transportEANForStorePack,
      consumer_size_to_order_size_ratio,
      transportQtyOfNextLowerItem,
      palletLayerQtyOfNextLowerItem,
      palletQtyOfNextLowerItem,
    } = mappedData;

    // Transport Pack/Qty of base item
    // Packaging-quantity bypass: when the corresponding v3.1 column wrote a value, skip derivation.
    const isSkipTransportQtyDerivation =
      mappedData._runtimeFlags?._skipTransportQtyDerivation &&
      !isBlank(mappedData._prasedExcelData.transportQtyOfBaseItem);
    if (
      !isSkipTransportQtyDerivation &&
      transportEANForStorePack &&
      consumer_size_to_order_size_ratio &&
      transportQtyOfNextLowerItem
    ) {
      mappedData.transportQtyOfBaseItem = consumer_size_to_order_size_ratio * transportQtyOfNextLowerItem;
    }

    // Pall/Pallet Layer/Qty of base item
    if (transportEANForStorePack) {
      if (mappedData.transportQtyOfBaseItem && palletLayerQtyOfNextLowerItem) {
        mappedData.palletLayerQtyOfBaseItem = mappedData.transportQtyOfBaseItem * palletLayerQtyOfNextLowerItem;
      }
    } else {
      if (consumer_size_to_order_size_ratio && palletLayerQtyOfNextLowerItem) {
        mappedData.palletLayerQtyOfBaseItem = consumer_size_to_order_size_ratio * palletLayerQtyOfNextLowerItem;
      }
    }

    // Pall/Pallet /Qty of base item
    // Packaging-quantity bypass: when the corresponding v3.1 column wrote a value, skip derivation.
    const isSkipPalletQtyDerivation =
      mappedData._runtimeFlags?._skipPalletQtyDerivation && !isBlank(mappedData._prasedExcelData.palletQtyOfBaseItem);
    if (
      !isSkipPalletQtyDerivation &&
      mappedData.palletLayerQtyOfBaseItem &&
      palletQtyOfNextLowerItem
    ) {
      mappedData.palletQtyOfBaseItem = mappedData.palletLayerQtyOfBaseItem * palletQtyOfNextLowerItem;
    }
  }

  return computedDataList;
};

export const correctCorrespondingValues = (dataList: IProductExcel[]) => {
  const mappedDataList = cloneDeep(dataList);

  for (let index = 0; index < mappedDataList.length; index++) {
    const mappedData = mappedDataList[index];

    // Questions
    if (mappedData.regulatedSubstance) {
      (mappedData.reachSubstancesAnnex19072006 as any) = 'true';
    }
    if (mappedData.informationHazardousSubstances) {
      (mappedData.hazardousSubstancesOver1 as any) = 'true';
    }
    if (mappedData.explanationNoUDI) {
      (mappedData.checkUniqueIdentification as any) = 'false';
    }
    if (mappedData.CMRSubstances) {
      (mappedData.cmrSubstance as any) = 'true';
    }
  }

  return mappedDataList;
};

export const getCellValue = (column: IImportColumn, data: IProductExcel) => {
  const { field } = column;
  if (column.field === 'foodlaCategory') {
    return data.foodlaCategory?.name;
  }
  if (column.field === 'batteryTypeOther') {
    const batteryTypeOtherItem = batteryTypes.find(({ value }) => value === BatteryTypeEnum.OTHER);
    const batteryType = data.batteryType?.toLowerCase();
    if (
      batteryType !== batteryTypeOtherItem?.displayName?.toLowerCase() &&
      batteryType !== BatteryTypeEnum.OTHER.toLowerCase()
    ) {
      return '';
    }
  }
  if (column.dataType === 'date') {
    const value = data[field] as Date;
    if (isNil(value)) return null;
    return value;
  }
  if (!isNil(data[field])) return data[field] as string | number;
  return null;
};

// CLP suffix case changes the meaning. Unresolved input must reach review intact,
// rather than accepting the known part of an unknown code or a mixed list.
const getClpCodes = (value: unknown, options: { value: string; displayName: string }[]): string[] | undefined => {
  if (isNil(value)) return undefined;
  const normalizeText = (text: string) => text.trim().replace(/\s+/g, ' ').replace(/\.$/, '');
  const codes: string[] = [];
  for (const item of Array.isArray(value) ? value : [value]) {
    if (typeof item !== 'string') return undefined;
    if (!item.trim()) continue;
    const codeText = item.trim().replace(/\s*\+\s*/g, '_');
    if (options.some(option => option.value === codeText)) {
      codes.push(codeText);
      continue;
    }
    const text = normalizeText(item);
    const matching = options.filter(option => {
      const wording = option.displayName.replace(/^(?:EUH|H|P)\d{3}[A-Za-z]*(?:\s*\+\s*(?:EUH|H|P)\d{3}[A-Za-z]*)*\s*\*?\s*/, '');
      return normalizeText(option.displayName) === text || normalizeText(wording).toLowerCase() === text.toLowerCase();
    });
    const matchingCodes = Array.from(new Set(matching.map(option => option.value)));
    if (matchingCodes.length === 1) {
      codes.push(matchingCodes[0]);
      continue;
    }
    if (matchingCodes.length > 1) return undefined;
    const tokens = codeText.split(/[\s,;]+/).filter(Boolean);
    if (!tokens.length || tokens.some(token => !options.some(option => option.value === token))) return undefined;
    codes.push(...tokens);
  }
  return Array.from(new Set(codes));
};

export const getHelpText = (column: IImportColumn, data: IProductExcel, rootCategory: IFoodlaCategoryOption) => {
  if (column.field !== 'basicDataTaxClassification' || String(getCellValue(column, data)).replace('%', '') !== '12') {
    return undefined;
  }
  const isFood = isNewFood(data.foodlaCategory, rootCategory);
  const snusOrTobacco = isSnusOrTobacco(data.foodlaCategory, rootCategory);

  if (isFood && !snusOrTobacco) {
    return '6% (livsmedel, tillfälligt, tidigare 12%)';
  }
  return undefined;
};

export const getEnumValue = (
  column: IImportColumn,
  data: IProductExcel,
  preferExact = false,
  options?: { returnUnmappedValue?: boolean },
) => {
  const { returnUnmappedValue } = options || {};
  const mapEnumValue = (
    enumDataListProp: { value: string | number; displayName: string }[],
    dataType: 'array' | 'string',
    mapOptions?: {
      mapValue?: (data: any) => string;
      mapReturnData?: (data: any) => any;
      exactMatch?: boolean;
      nullIfEmpty?: boolean;
    }
  ) => {
    const enumDataList = [...enumDataListProp].sort((a, b) => b.displayName.length - a.displayName.length);
    const { mapValue, mapReturnData, exactMatch, nullIfEmpty } = mapOptions || {};

    if (isNil(data[column.field])) return nullIfEmpty ? null : undefined;
    const value = mapValue ? mapValue(data[column.field]) : data[column.field];
    if (isNil(value)) return nullIfEmpty ? null : undefined;

    // Canonical state can already hold native codes. Keep those intact before the
    // template parser searches substrings (e.g. TWENTY must not consume TWENTY_FIVE).
    if (preferExact) {
      const values = Array.isArray(value) ? value : [value];
      const matches = values.map(item => enumDataList.find(
        option => String(item).toLowerCase() === String(option.value).toLowerCase()
      ));
      if (values.length && matches.every(Boolean)) {
        const exactValues = matches.map(item => item!.value);
        const exactValue = dataType === 'array' ? exactValues : exactValues[0];
        return mapReturnData ? mapReturnData(exactValue) : exactValue;
      }
    }

    const validEnumList: string[] = [];
    let checkingEnumValue = String(value).trim().toLowerCase();

    // An exact list matches whole items, so PIG never consumes part of PIGEON.
    const isExactList = exactMatch && dataType === 'array' && checkingEnumValue.split(/[,;|\n]/).length > 1;
    const isMatch = (expectedValue: string) => {
      if (!exactMatch) return checkingEnumValue.includes(expectedValue);
      if (!isExactList) return checkingEnumValue === expectedValue;
      return checkingEnumValue.split(/[,;|\n]/).some(item => item.trim() === expectedValue);
    };
    const removeMatch = (expectedValue: string) => {
      if (!isExactList) {
        checkingEnumValue = checkingEnumValue.replace(expectedValue, '');
        return;
      }
      const items = checkingEnumValue.split(/[,;|\n]/);
      items.splice(items.findIndex(item => item.trim() === expectedValue), 1);
      checkingEnumValue = items.join(',');
    };

    enumDataList.forEach(enumData => {
      const displayName = enumData.displayName.toLowerCase();
      const enumValue = String(enumData.value).toLowerCase();
      if (isMatch(displayName)) {
        removeMatch(displayName);
        validEnumList.push(String(enumData.value));
      } else if (isMatch(enumValue)) {
        removeMatch(enumValue);
        validEnumList.push(String(enumData.value));
      }
    });

    if (returnUnmappedValue) {
      return dataType === 'array'
        ? checkingEnumValue.replace(/^[,;|\s]+|[,;|\s]+$/g, '')
        : checkingEnumValue.trim();
    }

    if (dataType === 'array') {
      return mapReturnData ? mapReturnData(validEnumList) : validEnumList;
    } else if (dataType === 'string') {
      if (!validEnumList.length && nullIfEmpty) return null;
      return mapReturnData ? mapReturnData(validEnumList[0]) : validEnumList[0];
    }
  };

  if (column.field === 'descriptive_size_unit') {
    const descriptiveSizeUnitsEnumList = DESCRIPTIVE_SIZE_UNITS_MAP.map(item => ({
      value: item.enum,
      displayName: item.value,
    }));
    return mapEnumValue(descriptiveSizeUnitsEnumList, 'string', { exactMatch: true });
  }
  if (column.field === 'nutritional_unit') {
    const nutritionalUnitsEnumList = NUTRITIONAL_UNITS_MAP.map(item => ({
      value: item.enum,
      displayName: item.value,
    }));
    return mapEnumValue(nutritionalUnitsEnumList, 'string');
  }
  if (column.field === 'classification') {
    const val = PRODUCT_CLASSIFICATIONS.find(item => item.toLowerCase() === data.classification?.toLowerCase());
    return returnUnmappedValue ? (val ? '' : data.classification) : val;
  }

  if (column.field === 'securityNotices' || column.field === 'hazardStatements') {
    const groups: { listData: { value: string; displayName: string }[] }[] =
      column.field === 'securityNotices' ? securityNotices : hazardStatements;
    const codes = getClpCodes(data[column.field], groups.flatMap(({ listData }) => listData));
    // An unresolved list is already undefined; a resolved list leaves nothing unmapped.
    if (returnUnmappedValue) return codes ? '' : toString(data[column.field]);
    return codes;
  }
  if (column.field === 'animalCategoriesForFeed') {
    return mapEnumValue(animalCategoriesForFeed, 'array', { exactMatch: true });
  }
  if (column.field === 'animalFeedType') {
    return mapEnumValue(animalFeedTypes, 'string');
  }
  if (column.field === 'labels') {
    if (Array.isArray(data[column.field])) return returnUnmappedValue ? '' : data[column.field];
    const labelsEnumList = ALL_LABELS.map(({ title, field }) => ({ displayName: title, value: field }));
    return mapEnumValue(labelsEnumList, 'array', {
      mapReturnData: (list: string[]) => list.map(val => ({ field: val, state: true })),
    });
  }
  if (column.field === 'eggWeightClass') {
    const eggWeightClassEnumList = eggWeightClass
      .map(({ value }) => ({
        value,
        displayName: value.replace('_', '/'),
      }))
      .sort((prev, next) => (next.value.includes('_') ? 1 : 0) - (prev.value.includes('_') ? 1 : 0));
    return mapEnumValue(eggWeightClassEnumList, 'string');
  }
  if (column.field === 'hazardSymbols') return mapEnumValue(hazardSymbols, 'array');
  if (column.field === 'sunProtectionFactor') return mapEnumValue(sunProtectionFactor, 'string', { exactMatch: true });
  if (column.field === 'categoryWeightloss') return mapEnumValue(categoryWeightloss, 'string');
  if (column.field === 'preparedForm') return mapEnumValue(preparedForm, 'string', { exactMatch: true });
  if (column.field === 'signalWords') return mapEnumValue(signalWords, 'string');
  if (column.field === 'recyclingType') return mapEnumValue(recyclingType, 'string', { exactMatch: true });
  if (column.field === 'catchArea') return mapEnumValue(catchArea, 'string');
  if (column.field === 'fishCatchMethod') return mapEnumValue(fishCatchMethod, 'string', { exactMatch: true });
  if (column.field === 'fishProductionMethod') return mapEnumValue(fishProductionMethod, 'string');
  if (column.field === 'additionalCharacteristics') return mapEnumValue(additionalCharacteristics, 'array');
  if (column.field === 'containsBattery') return mapEnumValue(containsBatteries, 'string', { nullIfEmpty: true });
  if (column.field === 'approvalDietFeed') return mapEnumValue(approvalDietFeed, 'string', { nullIfEmpty: true });
  if (column.field === 'complianceMetalsSkinContact') return mapEnumValue(complianceMetalsSkinContact, 'string', { nullIfEmpty: true });
  if (column.field === 'confirmGuidanceSwedishElectronics')
    return mapEnumValue(confirmGuidanceSwedishElectronics, 'string', { nullIfEmpty: true });
  if (column.field === 'qrNutritionClaimsCode') return mapEnumValue(qrNutritionClaimsCode, 'string', { nullIfEmpty: true });
  if (column.field === 'qrHealthClaimsCode') return mapEnumValue(qrHealthClaimsCode, 'string', { nullIfEmpty: true });
  if (column.field === 'qrElectronicCode') return mapEnumValue(qrElectronicCode, 'string', { nullIfEmpty: true });
  if (column.field === 'complianceBiocid') return mapEnumValue(complianceBiocid, 'string', { nullIfEmpty: true });
  if (column.field === 'complianceBiocidalTreated') return mapEnumValue(complianceBiocidalTreated, 'string', { nullIfEmpty: true });
  if (column.field === 'complianceSunscreenRegulation') return mapEnumValue(complianceSunscreenRegulation, 'string');
  if (column.field === 'complianceToyDirective') return mapEnumValue(complianceToyDirective, 'string', { nullIfEmpty: true });
  if (column.field === 'ironing') return mapEnumValue(ironings, 'string');
  if (column.field === 'batteryType') return mapEnumValue(batteryTypes, 'string', { exactMatch: true });
  if (column.field === 'batteryTechnology') return mapEnumValue(batteryTechnologies, 'string', { exactMatch: true });
  if (column.field === 'batteryHazardousMetals') return mapEnumValue(batteryHazardousMetals, 'array');
  if (column.field === 'lampEnergyClass') return mapEnumValue(energyClassOptions, 'string', { exactMatch: true });
  if (column.field === 'dagsdosUnit') return mapEnumValue(dagsdosUnit, 'string');
  if (column.field === 'flammableSubtype') return mapEnumValue(flammableSubtype, 'string', { nullIfEmpty: true });
  if (column.field === 'other_substances') return mapEnumValue(supplementSubstanceUnits, 'array', { exactMatch: true });

  const { field, isEnumType } = column;
  const checkingValue = getCellValue(column, data);
  if (!isEnumType || isNil(checkingValue)) return checkingValue;

  const allowedValues = PRODUCT_ENUM_FIELD_VALUES[field]?.find(
    item => toString(item.value) === toString(checkingValue)
  );
  if (returnUnmappedValue) return allowedValues ? '' : toString(checkingValue);
  if (!allowedValues) return undefined;
  if (allowedValues) return preferExact ? (allowedValues.convertTo ?? allowedValues.value) : (allowedValues.convertTo || allowedValues.value);
  return null;
};

export const convertStringToBoolean = (valueParam: string, field?: keyof IProduct) => {
  if (isNil(valueParam)) return null;

  const value = toString(valueParam).trim().toLowerCase();

  if (field) {
    const booleanOption = PRODUCT_BOOLEAN_FIELD_VALUES[field]?.find(item => item.valueList.includes(value));
    if (booleanOption) return booleanOption.convertTo;
  }

  const booleanOption = PRODUCT_BOOLEAN_FIELD_VALUES.all.find(item => item.valueList.includes(value));
  if (booleanOption) return booleanOption.convertTo;

  return null;
};

export const convertBooleanValue = (column: IImportColumn, data: IProductExcel) => {
  const { field, dataType } = column;
  const checkingValue = getCellValue(column, data);
  if (dataType !== 'boolean' || checkingValue === undefined) return checkingValue;
  return convertStringToBoolean(String(checkingValue), field);
};

export const isValidEANValue = ({ field }: Pick<IImportColumn, 'field'>, data?: IProduct) => {
  let result = false;

  if (!data || !field) return result;

  const ean = data[field as keyof IProduct];

  const eanStr = toString(ean).trim();

  const storePackEanField = [
    'storePackEANForStorePack',
    'transportEANForStorePack',
    'palletLayerEANForStorePack',
    'palletEANForStorePack',
  ];

  // logistical packaging EANs are not required
  if (storePackEanField.includes(field) && isBlank(eanStr)) result = true;
  else if (!/^\d+$/.test(eanStr)) result = false;
  else if (field === 'EAN') result = [8, 13].includes(eanStr.length);
  else if (field && storePackEanField.includes(field)) result = eanStr.length === 14;
  else result = [8, 12, 13, 14, 18].includes(eanStr.length);

  if (result) return result;

  // only when [palletOrderableUnit] or [palletDespatchUnit] are "TRUE".
  if (field === 'palletEANForStorePack') {
    const palletOrderableUnitValue = String(data['palletOrderableUnit']) || '';
    const palletOrderableUnit = convertStringToBoolean(palletOrderableUnitValue, 'palletOrderableUnit');

    const palletDespatchUnitValue = String(data['palletDespatchUnit']) || '';
    const palletDespatchUnit = convertStringToBoolean(palletDespatchUnitValue, 'palletDespatchUnit');

    return result || (!palletOrderableUnit && !palletDespatchUnit);
  }

  // only when [transportOrderableUnit] or [transportDespatchUnit] are "TRUE".
  if (field === 'transportEANForStorePack') {
    const transportOrderableUnitValue = String(data['transportOrderableUnit']) || '';
    const transportOrderableUnit = convertStringToBoolean(transportOrderableUnitValue, 'transportOrderableUnit');

    const transportDespatchUnitValue = String(data['transportDespatchUnit']) || '';
    const transportDespatchUnit = convertStringToBoolean(transportDespatchUnitValue, 'transportDespatchUnit');

    return result || (!transportOrderableUnit && !transportDespatchUnit);
  }

  // only when [storePackOrderableUnit] or [storePackDespatchUnit] are "TRUE".
  if (field === 'storePackEANForStorePack') {
    const storePackOrderableUnitValue = String(data['storePackOrderableUnit']) || '';
    const storePackOrderableUnit = convertStringToBoolean(storePackOrderableUnitValue, 'storePackOrderableUnit');

    const storePackDespatchUnitValue = String(data['storePackDespatchUnit']) || '';
    const storePackDespatchUnit = convertStringToBoolean(storePackDespatchUnitValue, 'storePackDespatchUnit');

    return result || (!storePackOrderableUnit && !storePackDespatchUnit);
  }

  return result;
};

export const isWrongTypeCell = (column: IImportColumn, data: IProductExcel) => {
  const { dataType, isEnumType } = column;

  const checkingValue = getCellValue(column, data);

  if (isNil(checkingValue)) return false;
  if (column.field === 'descriptive_size_amount_extra' && Array.isArray(checkingValue)) {
    return checkingValue.some(value => typeof value !== 'number' || !Number.isFinite(value));
  }
  if (column.field === 'apoteaStorePriceNok' && isBlank(checkingValue)) return false;
  // "Ej valt" writes an empty string into the row (the radio/select option has
  // value ""). For the nullable quality fields that is a legitimate answer, not
  // a malformed one — an unanswered mandatory field is still caught below by
  // the mandatory + empty check.
  if (NULLABLE_FIELDS.includes(column.field) && isBlank(checkingValue)) return false;
  if (PRODUCT_EAN_FIELDS.includes(column.field)) {
    return !isValidEANValue({ field: column.field }, { ...data, [column.field]: checkingValue as string });
  }
  if (isEnumType) {
    return isNil(getEnumValue(column, data));
  }
  if (dataType === 'date') {
    return !checkingValue || !isDate(checkingValue as number);
  }
  if (dataType === 'number') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const regex = /^([-+]?\d+(?:[,.]\d+)?)$/gimu;
    const valueToString = toString(checkingValue).trim();
    return !regex.test(valueToString);
  }
  if (dataType === 'boolean') {
    return isNil(convertBooleanValue(column, data));
  }
  return false;
};

export const isEmptyCell = (column: IImportColumn, data: IProductExcel, options?: { byTitle?: boolean }) => {
  if (column.field === 'foodlaCategory') {
    return isNil(data.foodlaCategory);
  }
  const value = options?.byTitle ? data._excelData?.[column.title as keyof IProduct] : getCellValue(column, data);
  if (column.field === 'descriptive_size_amount_extra' && Array.isArray(value)) return value.length === 0;
  return isNil(value) || value === '';
};

export const parseDocumentUrls = (documentUrls?: string) => {
  return (documentUrls || '').split(';').map(item => item.trim()).filter(item => !isBlank(item));
}

const NUMBER_PATTERN = /^[-+]?(?:\d+(?:[.,]\d+)?|[.,]\d+)$/;
const ALCOHOL_PERCENTAGES = getPercentageValues().map(Number);

/** A well-formed number (decimal comma or point) as a number; anything else stays as entered for review. */
export const parseNumberValue = (value: unknown) => {
  const text = toString(value).trim();
  return NUMBER_PATTERN.test(text) ? Number(text.replace(',', '.')) : value;
};

/** The product form's input rules for a reviewed number: decimals, whole numbers, sign and listed values. */
export const getNumberValueError = (field: keyof IProduct, data: IProductExcel) => {
  const value = data[field];
  if (!NUMBER_RULE_FIELDS.includes(field) || isNil(value) || isBlank(toString(value))) return '';
  // A saved value the reviewer left untouched has already been through the product form.
  if (data._savedData && value === data._savedData[field]) return '';
  const number = parseNumberValue(value);
  if (typeof number !== 'number') return 'Ange ett giltigt tal.';
  if (FLOAT_FIELDS.includes(field) && number < 0) return 'Värdet får inte vara negativt.';
  if (INTEGER_FIELDS.includes(field) && !Number.isInteger(number)) return 'Ange endast heltal.';
  if (field === 'alcoholPercentage' && !ALCOHOL_PERCENTAGES.includes(number)) return 'Välj ett värde i listan.';
  return '';
};

export const isInvalidValueCell = (column: IImportColumn, data: IProductExcel) => {
  const value = getCellValue(column, data);

  // A blank answer to a nullable quality field means "Ej valt", so it must not
  // be reported as an invalid enum value.
  if (NULLABLE_FIELDS.includes(column.field) && isBlank(value)) return false;

  if (column.field === 'documentUrls') {
    const urls = parseDocumentUrls(data.documentUrls);
    return !urls.every((url) => !!getNormalizedDocumentUrl(url).normalized);
  }

  if (NUMBER_RULE_FIELDS.includes(column.field)) {
    return !!getNumberValueError(column.field, data);
  }

  // Only the amounts the product form offers can be saved.
  if (column.field === 'recycling_amount') {
    return !isBlank(value) && !RECYCLING_AMOUNT.some(item => item.value === Number(toString(value).replace(',', '.')));
  }

  if (column.field === 'apoteaStorePriceNok') {
    return Number(toString(value).replace(',', '.')) < 0;
  }

  if (['apoteaPurchasePrice', 'apoteaListPrice', 'apoteaStorePrice', 'lavQuantity'].includes(column.field)) {
    return !isNaN(Number(value)) && Number(value) < 0;
  }

  // The AI import copies a supplied sales description verbatim (or translates
  // it faithfully) rather than shortening it to fit, so an over-long value can
  // reach this step. Flag it here so the row blocks the import and the producer
  // shortens it themselves — same 155-character limit the product form enforces.
  if (column.field === 'shortSalesDescription') {
    return toString(value).length > SHORT_SALES_DESCRIPTION_MAX_LENGTH;
  }

  if (column.field === 'commodityCode') {
    const trimmedValue = (value as string)?.trim();
    if (!isBlank(trimmedValue) && column.field === 'commodityCode') {
      return !/^\d{8}$/.test(String(trimmedValue));
    }
  }

  if (['gross_weight_num', 'gross_weight', 'gross_height', 'gross_width', 'gross_depth'].includes(column.field)) {
    return checkDecimal(value as string | number);
  }

  if (column.field === 'country_of_manufacturing_string') {
    const country = String(value ?? '').trim();
    // Blank is valid — this field is optional except for medical-tech. Without
    // this guard an empty cell flags red (getCountryCode('') is falsy).
    return !isBlank(country) && !getCountryCode(country);
  }
  // not check when value is [null] or [undefined] or [empty]
  // -> this case should be check by [isEmptyCell]
  if (!isNil(value) && !!String(value) && column.isEnumType) {
    const enumValue = getEnumValue(column, data);
    const unmappedValue = getEnumValue(column, data, false, { returnUnmappedValue: true });
    return isNil(enumValue) || (Array.isArray(enumValue) && enumValue.length === 0) || !isBlank(unmappedValue);
  }
  return false;
};

export const isMandatoryCell = (column: IImportColumn, data: IProductExcel) => {
  if (data._saveRequiredFields) return data._saveRequiredFields.includes(column.field);
  if (column.field === 'regulatedSubstance') {
    return convertStringToBoolean(
      data.reachSubstancesAnnex19072006 as unknown as string,
      'reachSubstancesAnnex19072006'
    );
  }
  if (column.field === 'informationHazardousSubstances') {
    return convertStringToBoolean(data.hazardousSubstancesOver1 as unknown as string, 'hazardousSubstancesOver1');
  }
  if (column.field === 'explanationNoUDI') {
    return !convertStringToBoolean(data.checkUniqueIdentification as unknown as string, 'checkUniqueIdentification');
  }
  if (column.field === 'CMRSubstances') {
    return convertStringToBoolean(data.cmrSubstance as unknown as string, 'cmrSubstance');
  }

  // Nutritional
  if (['fett', 'mattatFett', 'kolhydrat', 'sockerarter', 'protein', 'salt'].includes(column.field)) {
    const isHasKjValue = !isNil(data.energi_kj) && !!String(data.energi_kj).trim();
    const isHasKcalValue = !isNil(data.energi_kcal) && !!String(data.energi_kcal).trim();
    return isHasKjValue || isHasKcalValue;
  }

  // Signal Words
  if (['hazardSymbols', 'hazardStatements', 'securityNotices'].includes(column.field)) {
    return ['fara', 'varning'].includes(String(data.signalWords || '').toLowerCase());
  }

  return column.isMandatory;
};

export const isEanDuplicated = (data: IProductExcel, dataList: IProductExcel[]) => {
  if (isBlank(data.EAN)) return false;
  return dataList.filter(item => item.EAN === data.EAN).length > 1;
};

/** The entered EAN is reserved by another producer's product or draft (see FC-2127). */
export const isEanConflict = (data: IProductExcel) =>
  !!data._conflictEan && data._conflictEan === String(data.EAN ?? '').trim();

export const isEanExist = (column: IImportColumn, data: IProductExcel, username: string) => {
  if (column.field !== 'EAN') return false;
  if (isEanConflict(data)) return true;
  if (!data._savedDataForCheckEAN?.producer?.username) return false;
  return data._savedDataForCheckEAN.producer.username !== username && data._savedDataForCheckEAN.EAN === data.EAN;
};

export const isErrorCell = (column: IImportColumn, data: IProductExcel) => {
  if (data._saveValidationErrors?.[column.field]) return true;
  // Grouped native rules may be answered by a different field in the group.
  if (data._saveValidationErrors && isEmptyCell(column, data)) return false;
  let result =
    isWrongTypeCell(column, data) ||
    isInvalidValueCell(column, data) ||
    (!data._saveValidationErrors && isMandatoryCell(column, data) && isEmptyCell(column, data));

  if (!result) return false;

  if (PRODUCT_EAN_FIELDS.includes(column.field)) {
    return !isValidEANValue(column, data);
  }

  return true;
};

export const getErrorCell = (column: IImportColumn, data: IProductExcel) => {
  if (data._saveValidationErrors?.[column.field]) return data._saveValidationErrors[column.field]!;
  if (!isErrorCell(column, data) || column.field === 'image_src') return '';
  const isEmpty = isEmptyCell(column, data);

  if (isEmpty) return 'Vänligen fyll i detta fält.';

  const numberError = getNumberValueError(column.field, data);
  if (numberError) return numberError;

  if (['gross_weight_num', 'gross_weight', 'gross_height', 'gross_width', 'gross_depth'].includes(column.field)) {
    const value = getCellValue(column, data);
    if (checkDecimal(value as string | number)) {
      return 'Ange endast heltal.';
    }
  }

  if (column.field === 'basicDataTaxClassification') {
    return 'Välj skattesats 6, 12 eller 25 %.';
  }

  if (column.field === 'shortSalesDescription') {
    return `Max ${SHORT_SALES_DESCRIPTION_MAX_LENGTH} tecken`;
  }

  if (column.field === 'recycling_amount') {
    return `Tillåtna värden: ${RECYCLING_AMOUNT.map(({ value }) => value).join(', ')}`;
  }

  if (column.field === 'hazardStatements' || column.field === 'securityNotices') {
    return 'Kontrollera CLP-koderna. Ange hela koder med rätt stora och små bokstäver.';
  }

  return 'Ogiltigt värde.';
};

export const getErrorRowCells = (columnList: IImportColumn[], data: IProductExcel) => {
  const errorList: { message: string; column: IImportColumn; data: IProductExcel }[] = [];

  columnList.forEach(column => {
    const errorMessage = getErrorCell(column, data);
    if (errorMessage) {
      errorList.push({ message: errorMessage, column, data });
    }
  });

  return errorList;
};

export const checkEnumType = (field: keyof IProduct): boolean => {
  return PRODUCT_ENUM_FIELD.includes(field);
};

export const getDataType = (field: keyof IProduct): DataType => {
  if (PRODUCT_DATE_FIELD.includes(field)) return 'date';
  if (PRODUCT_NUMBER_FIELD.includes(field)) return 'number';
  if (PRODUCT_BOOLEAN_FIELD.includes(field)) return 'boolean';
  return 'string';
};

export const checkSameWithSavedBooleanValue = (column: IImportColumn, data: IProductExcel) => {
  const { _excelData, _savedData, _revertedSavedData } = data;

  if (!_savedData || !_revertedSavedData) return false;

  const oldValue = _savedData[column.field];
  const newValue = _excelData[column.title];

  return convertStringToBoolean(String(newValue), column.field) === oldValue;
};

export const checkSameWithSavedValue = (column: IImportColumn, data: IProductExcel) => {
  const { _excelData, _savedData, _revertedSavedData } = data;

  if (!_savedData || !_revertedSavedData) return false;

  const oldValue = _savedData[column.field];
  const newValue = _excelData[column.title];

  // special field
  if (column.field === 'foodlaCategory') {
    return (oldValue as IFoodlaCategory)?.name === newValue;
  }
  if (column.field === 'title') {
    return capitalizeTitle(toString(newValue)) === capitalizeTitle(toString(oldValue));
  }
  if (column.field === 'search_terms') {
    return oldValue === newValue;
  }
  if (column.field === 'article') {
    return [_savedData.article, _savedData.purchasingDataSupplierArticleNo].includes(toString(newValue));
  }
  if (column.field === 'country_of_manufacturing_string') {
    return getCountryCode(newValue as string) === _savedData.country_of_manufacturing;
  }

  if (isNil(oldValue)) return false;

  // Date
  if (column.dataType === 'date') {
    let valueTimestamp = Infinity;
    if (isDate(newValue)) {
      valueTimestamp = utcToZonedTime(newValue as Date, 'Europe/Stockholm').getTime();
    }
    return valueTimestamp === oldValue;
  }

  // Enum
  if (column.isEnumType) {
    return getEnumValue(column, { ...data, [column.field]: newValue }) === oldValue;
  }

  // Boolean
  if (column.dataType === 'boolean') {
    return checkSameWithSavedBooleanValue(column, data);
  }

  // Number
  if (column.dataType === 'number') {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const regex = /^([-+]?\d+(?:[,.]\d+)?)$/gimu;
    const stringValue = toString(newValue).trim().replace(',', '.');
    const numberValue = parseFloat(stringValue);
    return stringValue.match(regex) && toString(numberValue) === toString(oldValue);
  }

  // String
  return toString(newValue) === toString(oldValue);
};

export const checkValueReviewed = (column: IImportColumn, data: IProductExcel) => {
  if (!data?._savedData) return false;
  const savedData = data._savedData;

  const isHasValue = hasValue(savedData[column.field]);
  const onlyRequireAndNeedApproveFields = [
    'title',
    'ingredient_statement',
    'short_text',
    'energi_kj',
    'energi_kcal',
    'fett',
    'mattatFett',
    'kolhydrat',
    'sockerarter',
    'protein',
    'salt',
    'trade_item_temperature_information',
    'place_of_item_activity',
    'brand',
    'brand_food',
    'search_terms',
  ] as (keyof IProduct)[];

  // check if their value are empty
  if (onlyRequireAndNeedApproveFields.includes(column.field)) {
    return isHasValue;
  }

  if (column.field === 'EAN') {
    const { eanLengthError, eanControlError, eanWrongCoopStoreError, nonUniqError } = checkEANValue(savedData, true);
    return (
      isHasValue &&
      !(
        (!!eanLengthError && !savedData.ignoreEANLengthError) ||
        !!eanControlError ||
        !!eanWrongCoopStoreError ||
        !!nonUniqError
      )
    );
  }

  // gross_weight, gross_weight_num
  if ((['gross_weight', 'gross_weight_num'] as (keyof IProduct)[]).includes(column.field)) {
    return isHasValue && !checkGrossWeightError(savedData);
  }

  // descriptive_size_amount
  if (column.field === 'descriptive_size_amount') {
    return isHasValue && !checkDescriptiveError(savedData);
  }

  // GrossDimensions
  if (column.field === 'gross_height') {
    return (
      isHasValue &&
      !checkDecimal(savedData.gross_height) &&
      !(!!checkRequireDimenstions(savedData) && !String(savedData.gross_height))
    );
  }
  if (column.field === 'gross_width') {
    return (
      isHasValue &&
      !checkDecimal(savedData.gross_width) &&
      !(!!checkRequireDimenstions(savedData) && !String(savedData.gross_width))
    );
  }
  if (column.field === 'gross_depth') {
    return (
      isHasValue &&
      !checkDecimal(savedData.gross_depth) &&
      !(!!checkRequireDimenstions(savedData) && !String(savedData.gross_depth))
    );
  }

  return false;
};
